<template>
  <div :class="$style.container">
    <div :class="$style.header">
      <h3 :class="$style.title">日程調整ごとのデフォルト設定</h3>
      <FlatButton @click="openDefaultCalendarSettingsModal">
        <template #icon>
          <IconPenLine />
        </template>
        編集
      </FlatButton>
    </div>

    <table :class="$style.settingsTable">
      <thead>
        <tr>
          <th>日程調整タイプ</th>
          <th>使用カレンダー</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="setting in processedCalendarSettings"
          :key="setting.variantValue"
        >
          <td>{{ setting.variantLabel }}</td>
          <td>
            <router-link v-if="setting.isLink" :to="setting.routeTo!">
              {{ setting.displayText }}
            </router-link>
            <span v-else :class="setting.classObject">
              {{ setting.displayText }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <DefaultCalendarSettingsModalWrapper>
      <DefaultCalendarSettingsModal
        :defaultCalendars="defaultCalendars"
        :availableCalendars="availableCalendars"
        @save="handleSave"
        @cancel="closeDefaultCalendarSettingsModal"
      />
    </DefaultCalendarSettingsModalWrapper>
  </div>
</template>

<script setup lang="ts">
import { computed, useCssModule } from 'vue'
import { useRoute } from 'vue-router'
import FlatButton from '@/components/atoms/FlatButton.vue'
import IconPenLine from '@/components/icons/IconPenLine.vue'
import { useModal } from '@/hooks/useModal'
import { useAlert } from '@/libs/alertPortal/index.ts'
import { useLogger } from '@/logger/logger'
import {
  ScheduleAdjustmentVariant,
  type CalendarSummary,
  type ScheduleAdjustmentDefaultCalendarInput,
} from '@/types/schema.generated.ts'

import DefaultCalendarSettingsModal from './DefaultCalendarSettingsModal.vue'
import {
  useFetchScheduleAdjustmentDefaultCalendarsQuery,
  useSaveScheduleAdjustmentDefaultCalendarsMutation,
  type FetchScheduleAdjustmentDefaultCalendarsQuery,
} from './DefaultCalendarSettings.generated.ts'
import { scheduleAdjustmentVariants } from './calendar.ts'

const props = defineProps<{
  availableCalendars: CalendarSummary[]
}>()

const {
  openDefaultCalendarSettingsModal,
  closeDefaultCalendarSettingsModal,
  DefaultCalendarSettingsModal: DefaultCalendarSettingsModalWrapper,
} = useModal('defaultCalendarSettings')

const $style = useCssModule()

// デフォルト設定の取得
const { result: defaultCalendarsResult, refetch: refetchDefaultCalendars } =
  useFetchScheduleAdjustmentDefaultCalendarsQuery({
    fetchPolicy: 'no-cache',
  })

const defaultCalendars = computed<ScheduleAdjustmentDefaultCalendarInput[]>(
  () => {
    if (!defaultCalendarsResult.value) {
      return []
    }

    return defaultCalendarsResult.value.scheduleAdjustmentDefaultCalendars.map(
      (
        c: FetchScheduleAdjustmentDefaultCalendarsQuery['scheduleAdjustmentDefaultCalendars'][number]
      ) => ({
        CalendarGUID: c.CalendarGUID,
        ScheduleAdjustmentVariant: c.ScheduleAdjustmentVariant,
      })
    )
  }
)

const getCalendarInfo = (
  variant: string
): {
  name: string
  guid: string | null
  isOrphaned: boolean
  isSet: boolean
} => {
  const defaultCalendar = defaultCalendars.value.find(
    calendar => calendar.ScheduleAdjustmentVariant === variant
  )

  if (!defaultCalendar || !defaultCalendar.CalendarGUID) {
    return { name: '未設定', guid: null, isOrphaned: false, isSet: false }
  }

  const calendar = props.availableCalendars.find(
    c => c.guid === defaultCalendar.CalendarGUID
  )

  if (!calendar) {
    // The calendar GUID from settings is not found in available calendars (orphaned)
    return {
      name: '（削除されたカレンダー）',
      guid: null,
      isOrphaned: true,
      isSet: true,
    }
  }

  // Calendar is set and found in available calendars
  return {
    name: calendar.title,
    guid: calendar.guid,
    isOrphaned: false,
    isSet: true,
  }
}

type ProcessedCalendarSetting = {
  variantLabel: string
  variantValue: ScheduleAdjustmentVariant
  displayText: string
  isLink: boolean
  routeTo?: {
    name: 'calendarSettingDetail'
    params: { calendarGUID: string; entranceGUID: string }
  }
  classObject: Record<string, boolean>
}

const route = useRoute('calendarSetting')

const processedCalendarSettings = computed<ProcessedCalendarSetting[]>(() => {
  return scheduleAdjustmentVariants.map(variant => {
    const info = getCalendarInfo(variant.value)
    const isLink = info.isSet && !info.isOrphaned && !!info.guid

    const displayText = (() => {
      if (info.isOrphaned && info.isSet) {
        return `${info.name} - 再設定してください`
      }

      if (variant.value === ScheduleAdjustmentVariant.Default && !info.isSet) {
        return '日程調整機能のご利用前に設定が必要です'
      }

      return info.name
    })()

    return {
      variantLabel: variant.label,
      variantValue: variant.value,
      displayText,
      isLink,
      routeTo:
        isLink && info.guid
          ? {
              name: 'calendarSettingDetail',
              params: {
                calendarGUID: info.guid,
                entranceGUID: route.params.entranceGUID,
              },
            }
          : undefined,
      classObject: {
        [$style.calendarOrphanedText]: info.isOrphaned,
        [$style.calendarDefaultNotSetText]:
          variant.value === ScheduleAdjustmentVariant.Default && !info.isSet,
      },
    }
  })
})

// 保存処理
const { mutate: saveDefaultCalendars } =
  useSaveScheduleAdjustmentDefaultCalendarsMutation()

const alert = useAlert()
const logger = useLogger()

const handleSave = async (
  updatedDefaultCalendars: ScheduleAdjustmentDefaultCalendarInput[]
) => {
  try {
    logger.ltlPeople.event.saveScheduleAdjustmentDefaultCalendars(
      logger.buildMeta(),
      {
        data: {
          defaultCalendars: updatedDefaultCalendars.map(c => ({
            calendarGUID: c.CalendarGUID,
            scheduleAdjustmentVariant: c.ScheduleAdjustmentVariant,
          })),
        },
      }
    )

    await saveDefaultCalendars({
      input: {
        ScheduleAdjustmentDefaultCalendars: updatedDefaultCalendars,
      },
    })

    await refetchDefaultCalendars()

    alert.success('日程調整のデフォルト設定を保存しました')
  } catch (error) {
    alert.error('処理に失敗しました')
    console.error('Failed to save or refetch default calendars:', error)
  } finally {
    closeDefaultCalendarSettingsModal()
  }
}
</script>

<style lang="scss" module>
.container {
  margin-bottom: 24px;
  padding: 16px;
  border: 1px solid $color-border-low-emphasis;
  border-radius: 8px;
  background-color: $color-surface-primary;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.title {
  font-size: 16px;
  font-weight: bold;
  margin: 0;
}

.settingsTable {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 8px 12px;
    text-align: left;
    border-bottom: 1px solid $color-border-low-emphasis;
  }

  th {
    font-weight: bold;
    color: $color-text-and-object-high-emphasis;
  }

  td {
    color: $color-text-and-object-medium-emphasis;
  }
}

.calendarOrphanedText {
  color: $color-text-and-object-accent-red;
}

.calendarDefaultNotSetText {
  color: $color-text-and-object-accent-red;
}
</style>
